import React, { useCallback, useMemo, useState } from 'react';
import { StageScreen, StagePopup } from './components/StageShell';

const DEFAULT_VOTE_VALUE = 20;
const MOBILE_VOTE_ADVANCE_DELAY_MS = 5000;
const DESKTOP_VOTE_ADVANCE_DELAY_MS = 300;

function popupHasContent(image, text) {
  return Boolean(String(image ?? '').trim()) || Boolean(String(text ?? '').trim());
}

function getVoteAdvanceDelay() {
  if (typeof window === 'undefined' || !window.matchMedia) return MOBILE_VOTE_ADVANCE_DELAY_MS;
  return window.matchMedia('(min-width: 768px)').matches
    ? DESKTOP_VOTE_ADVANCE_DELAY_MS
    : MOBILE_VOTE_ADVANCE_DELAY_MS;
}

function flattenTopicQuestions(stage) {
  const topics = Array.isArray(stage?.topics) ? stage.topics : [];
  const maxTopics = Math.min(10, Math.max(1, Number(stage?.max_topics) || 10));
  return topics.slice(0, maxTopics).map((topic, ti) => {
    const qs = Array.isArray(topic?.questions) ? topic.questions : [];
    const q = qs[0] || {};
    const options = (Array.isArray(q.options) ? q.options : []).slice(0, 2).map((opt, oi) => ({
      letter: oi === 0 ? 'A' : 'B',
      answer: opt?.answer || opt?.label || '',
      description: opt?.description || '',
      value: Math.max(0, Number(opt?.value) || DEFAULT_VOTE_VALUE),
    }));
    while (options.length < 2) {
      options.push({
        letter: options.length === 0 ? 'A' : 'B',
        answer: '',
        description: '',
        value: DEFAULT_VOTE_VALUE,
      });
    }
    return {
      heading: q.heading || q.question || '',
      topicIndex: ti,
      topicName: topic?.name || `Topic ${ti + 1}`,
      options,
    };
  });
}

function calcPercents(totals) {
  const total = totals.A + totals.B;
  if (total <= 0) return { A: 0, B: 0 };
  return {
    A: Math.round((totals.A / total) * 100),
    B: Math.round((totals.B / total) * 100),
  };
}

export default function Stage1Poll({ stage, pagesBg, onComplete }) {
  const questions = useMemo(() => flattenTopicQuestions(stage), [stage]);
  const startHasContent = popupHasContent(stage?.start_popup_image, stage?.start_popup_text);
  const endHasContent = popupHasContent(stage?.end_popup_image, stage?.end_popup_text);

  const [phase, setPhase] = useState(startHasContent ? 'start' : 'game');
  const [qIdx, setQIdx] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [totals, setTotals] = useState({ A: 0, B: 0 });
  const [activeLetter, setActiveLetter] = useState(null);
  const [hoverLetter, setHoverLetter] = useState(null);
  const [voting, setVoting] = useState(false);

  const q = questions[qIdx];
  const percents = calcPercents(totals);

  const finishGame = useCallback(() => {
    onComplete({
      answers,
      scoreA: totals.A,
      scoreB: totals.B,
    });
  }, [onComplete, answers, totals]);

  const handleVote = (opt) => {
    if (voting || !q) return;
    const letter = opt.letter;
    setVoting(true);
    setActiveLetter(letter);

    const nextAnswers = [
      ...answers,
      {
        answer: opt.answer,
        choice: letter,
        topic: q.topicName,
        heading: q.heading,
      },
    ];
    const nextTotals = {
      ...totals,
      [letter]: totals[letter] + (opt.value || DEFAULT_VOTE_VALUE),
    };

    setAnswers(nextAnswers);
    setTotals(nextTotals);

    setTimeout(() => {
      setActiveLetter(null);
      setVoting(false);
      const isLast = qIdx + 1 >= questions.length;
      if (!isLast) {
        setQIdx((i) => i + 1);
      } else if (endHasContent) {
        setPhase('end');
      } else {
        onComplete({
          answers: nextAnswers,
          scoreA: nextTotals.A,
          scoreB: nextTotals.B,
        });
      }
    }, getVoteAdvanceDelay());
  };

  const activeDesc =
    q?.options?.find((o) => o.letter === (activeLetter || hoverLetter))?.description || '';

  if (phase === 'start') {
    return (
      <StageScreen pagesBg={pagesBg} pageKey="stage_1" stage={stage}>
        <StagePopup
          image={stage?.start_popup_image}
          text={stage?.start_popup_text}
          onNext={() => setPhase('game')}
        />
      </StageScreen>
    );
  }

  if (phase === 'end') {
    return (
      <StageScreen pagesBg={pagesBg} pageKey="stage_1" stage={stage}>
        <StagePopup
          image={stage?.end_popup_image}
          text={stage?.end_popup_text}
          onNext={finishGame}
        />
      </StageScreen>
    );
  }

  if (!q) {
    return (
      <StageScreen pagesBg={pagesBg} pageKey="stage_1" stage={stage}>
        <div className="fi-stage-root fi-s1-root">
          <button type="button" className="fi-btn fi-btn--primary" onClick={() => onComplete({ answers: [], scoreA: 0, scoreB: 0 })}>
            Continue
          </button>
        </div>
      </StageScreen>
    );
  }

  return (
    <StageScreen pagesBg={pagesBg} pageKey="stage_1" stage={stage}>
      <div className="fi-stage-root fi-s1-root">
        <div className="fi-s1-question-box">
          {q.heading ? (
            <p className="fi-s1-question-text">{q.heading}</p>
          ) : (
            <p className="fi-s1-question-text">
              In 2047, what do you think{' '}
              <span className="fi-s1-question-highlight">{q.topicName}</span>{' '}
              would be known for?
            </p>
          )}
        </div>

        <div className="fi-s1-layout">
          <div className="fi-s1-main">
            <div className="fi-s1-options-row">
              {q.options.map((opt) => {
                const letter = opt.letter;
                const isActive = activeLetter === letter;

                return (
                  <button
                    key={letter}
                    type="button"
                    className={`fi-s1-option ${isActive ? 'fi-s1-option--active' : ''} ${voting ? 'fi-s1-option--locked' : ''}`}
                    disabled={voting}
                    onMouseEnter={() => setHoverLetter(letter)}
                    onMouseLeave={() => setHoverLetter(null)}
                    onClick={() => handleVote(opt)}
                  >
                    <span className="fi-s1-option__label">{opt.answer || `Option ${letter}`}</span>
                  </button>
                );
              })}
            </div>

            {activeDesc ? (
              <div className="fi-s1-desc-box">
                <p>{activeDesc}</p>
              </div>
            ) : null}
          </div>

          <aside className="fi-s1-stats" aria-label="Poll results">
            <div className="fi-s1-stats__pill">
              <div className="fi-s1-stats__row">
                <span className="fi-s1-stats__letter">A</span>
                <span className="fi-s1-stats__pct">{percents.A}%</span>
              </div>
              <div className="fi-s1-stats__bar">
                <div className="fi-s1-stats__bar-fill fi-s1-stats__bar-fill--a" style={{ width: `${percents.A}%` }} />
              </div>
            </div>
            <div className="fi-s1-stats__pill">
              <div className="fi-s1-stats__row">
                <span className="fi-s1-stats__letter">B</span>
                <span className="fi-s1-stats__pct">{percents.B}%</span>
              </div>
              <div className="fi-s1-stats__bar">
                <div className="fi-s1-stats__bar-fill fi-s1-stats__bar-fill--b" style={{ width: `${percents.B}%` }} />
              </div>
            </div>
          </aside>
        </div>

        <p className="fi-s1-progress">
          {qIdx + 1} / {questions.length}
        </p>
      </div>
    </StageScreen>
  );
}
